import { greekIt } from 'app/util/utils';
import { Link } from 'core/components';
import React from 'react';
import DeviceLink from 'app/components/links/DeviceLink';
import FilteredPolicyLink from 'app/components/links/FilteredPolicyLink';
import InterfaceLink from 'app/components/links/InterfaceLink';
import { kagentIdRenderer } from 'app/components/dataviews/views/legend/legendRenderers';

const getInterfaceNameDimensionColumn = (dimension, label) => ({
  name: dimension,
  label: label || dimension,
  minWidth: 60,
  flexBasis: 250,
  renderer: ({ value, model }) => {
    const ifIndex = model.get('ifindex');
    const deviceName = model.get('device_name');
    return deviceName && ifIndex ? (
      <InterfaceLink snmp_id={ifIndex} device_name={deviceName}>
        {value}
      </InterfaceLink>
    ) : (
      value
    );
  }
});

const getGenericDimensionColumn = (dimension, label) => ({
  name: dimension,
  label: label || dimension,
  minWidth: 60,
  flexBasis: 250,
  renderer: ({ value }) => value
});

const getInterfaceCounterDimensionColumn = ({ index }) => ({
  name: 'name',
  label: 'Interface Name',
  minWidth: 60,
  flexBasis: index === 0 ? 250 : 150,
  renderer: ({ model, value }) => {
    const ifIndex = model.get('ifindex');
    const deviceName = model.get('device_name');
    const target = deviceName && ifIndex && `/v4/infrastructure/devices/${deviceName}/interfaces/${ifIndex || ''}`;

    return target ? (
      <Link to={target} fontWeight="medium">
        {value}
      </Link>
    ) : (
      value
    );
  }
});

const getDeviceNameDimensionColumn = ({ index }) => ({
  name: 'device_name',
  label: 'Device Name',
  minWidth: 60,
  flexBasis: index === 0 ? 250 : 150,
  renderer: ({ model }) => <DeviceLink name={model.get('device_name')} fontWeight="medium" />
});

const getDeviceIdDimensionColumn = ({ index }) => ({
  name: 'device_id',
  label: 'Device ID',
  minWidth: 60,
  flexBasis: index === 0 ? 250 : 150,
  renderer: ({ model }) => <DeviceLink deviceId={model.get('device_id')} showId fontWeight="medium" />
});

const getPolicyDimensionColumn = ({ index }) => ({
  name: 'policy_id',
  label: 'Policy ID',
  minWidth: 60,
  flexBasis: index === 0 ? 250 : 150,
  renderer: ({ model }) => {
    const policyId = model.get('policy_id');
    return (
      <FilteredPolicyLink id={policyId} fontWeight="medium">
        {(policyModel) => `${policyModel?.get('name') ?? '---'} (${policyId})`}
      </FilteredPolicyLink>
    );
  }
});

const getKagentIdDimensionColumn = ({ dimension, label, index }) => {
  const renderKagentId = kagentIdRenderer(false);
  return {
    name: dimension,
    label: label || dimension,
    minWidth: 60,
    flexBasis: index === 0 ? 250 : 150,
    renderer: (props) => (props.value == null ? null : renderKagentId(props))
  };
};

const getSpeedDimensionColumn = () => ({
  label: 'Capacity',
  name: 'speed',
  id: 'speed',
  value: 'speed',
  renderer: ({ value }) => {
    const { displayFull } = greekIt(value * 1e6, { fix: 0, suffix: '' });
    return displayFull;
  }
});

export const getMetricsResultDimensionColumns = ({ decoratedQuery, fullSelectedMeasurement }) =>
  decoratedQuery.dimensions.map((dimension, index) => {
    const label = fullSelectedMeasurement?.get(`storage.Dimensions.${dimension}`)?.Label;
    const columnProps = { dimension, label, decoratedQuery, index };
    switch (dimension) {
      case 'device_name':
        return getDeviceNameDimensionColumn(columnProps);
      case 'device_id':
        return getDeviceIdDimensionColumn(columnProps);
      case 'policy_id':
        return getPolicyDimensionColumn(columnProps);
      case 'agent_id':
        return getKagentIdDimensionColumn(columnProps);
      case 'speed':
        return getSpeedDimensionColumn();
      case 'name':
        if (fullSelectedMeasurement?.get('measurement') === '/interfaces/counters') {
          return getInterfaceCounterDimensionColumn(columnProps);
        }
      // intentional fall through to default if above conditional fails
      case 'ifDescr':
      case 'description':
        return getInterfaceNameDimensionColumn(dimension, label);
      default:
        return getGenericDimensionColumn(dimension, label);
    }
  });
